'use client';
/* eslint-disable */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
   Box,
   Button,
   Flex,
   Text,
   useColorModeValue,
   useDisclosure,
   Select,
   useOutsideClick,
   useBreakpointValue,
   Input,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import 'jspdf-autotable';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { MdNavigateNext, MdNavigateBefore } from 'react-icons/md';
import MobileDataTable from '../../components/MobileDataTable';

// Components - consider lazy loading these
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import DynamicTable from '../../components/DynamicAllDataTable';
import DeleteDialog from '../../components/DeleteDialog';
import { useUser } from 'contexts';

// Lazy load the form component
const CountryMasterForm = React.lazy(() => import('./CountryMasterForm'));

// Action imports
import { deleteCountry, getCountries } from '../../../../../redux/actions/country';

// Utility imports
import { downloadPDF, downloadExcel, useServerPagination, usePagination } from '../../../../../utils/DataTableUtils';
import Loader from 'views/admin/default/Loader';

const columnHelper = createColumnHelper();
const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

export default function CountryMaster({ columnsData }) {
   // Theme values - memoize these
   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const backgroundColor = useColorModeValue('white', 'rgba(12, 20, 41, 1)');
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');

   // Redux
   const dispatch = useDispatch();
   const countryData = useSelector((state) => state.country.list || []);
   const totalCount = useSelector((state) => state.country.totalCount || 1);
   const pagination = useSelector((state) => state.country?.pagination);

   // State
   const [sorting, setSorting] = useState([]);
   const [data, setData] = useState([]);
   const [currentRow, setCurrentRow] = useState(null);
   const [searchQuery, setSearchQuery] = useState('');
   const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
   const [isEditMode, setIsEditMode] = useState(false);
   const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [selectedRowId, setSelectedRowId] = useState(null);
   const [currentPage, setCurrentPage] = useState(1);
   const [limit, setLimit] = useState(10);
   const [expanded, setExpanded] = useState(false);
   const [isEditingPage, setIsEditingPage] = useState(false);
   const [pageInputValue, setPageInputValue] = useState('');
   const [isLoading, setIsLoading] = useState(false);

   // Chakra hooks
   const { isOpen, onOpen, onClose } = useDisclosure();
   const isMobileOrTablet = useBreakpointValue({ base: true, md: false, lg: false });
   const wrapperRef = useRef();

   // User context and permissions
   const { user } = useUser();
   const Admin = user?.isAdmin;
   const companyId = Number(localStorage.getItem('companyId'));
   const isDesktop = useBreakpointValue({ base: false, md: true });
   // Memoize user rights to prevent unnecessary recalculations
   const countryRight = useMemo(() => {
      return Array.isArray(user?.userRights)
         ? user.userRights.find((right) => right.companyId === companyId)?.country
         : null;
   }, [user?.userRights, companyId]);

   // Memoize filtered data
   const filteredData = useMemo(() => {
      if (!searchQuery) return data;
      return data?.filter((row) =>
         columnsData?.some((col) => String(row[col.id]).toLowerCase().includes(searchQuery.toLowerCase())),
      );
   }, [data, searchQuery, columnsData]);

   // Pagination hooks
   const { getPaginationParams } = usePagination(data, filteredData);
   const { resetPage } = useServerPagination(totalCount);

   // Column visibility state
   const [columnVisibility, setColumnVisibility] = useState(
      columnsData.reduce((acc, column) => {
         acc[column.id] = true;
         return acc;
      }, {}),
   );

   // Reset to page 1 when search query changes
   useEffect(() => {
      setCurrentPage(1);
   }, [searchQuery]);

   const debounce = (func, delay) => {
      let timer;
      return function (...args) {
         clearTimeout(timer);
         timer = setTimeout(() => func.apply(this, args), delay);
      };
   };

   const debouncedSearchHandler = React.useCallback(
      debounce((query) => {
         setDebouncedSearchQuery(query);
      }, 500),
      [],
   );

   // Handle search input changes
   const handleSearchChange = (e) => {
      const value = e.target.value.trimStart();
      setSearchQuery(value);
      debouncedSearchHandler(value);
   };

   // Fetch data when dependencies change
   useEffect(() => {
      const fetchMainData = async () => {
         try {
            setIsLoading(true);
            await dispatch(
               getCountries({
                  page: currentPage,
                  // limit: limit === pagination?.totalProducts ? undefined : limit,
                  search: debouncedSearchQuery,
                  sortBy: sorting.length ? sorting[0]?.name : 'name',
                  sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
               }),
            );
         } catch (error) {
            console.error('Error fetching main data:', error);
         } finally {
            setIsLoading(false);
         }
      };

      // Debounce search to prevent excessive API calls
      const timerId = setTimeout(fetchMainData, 300);

      return () => clearTimeout(timerId);
   }, [dispatch, currentPage, limit, debouncedSearchQuery, sorting, pagination?.totalProducts]);

   // Update local data when redux data changes
   useEffect(() => {
      if (!limit) {
         setData(countryData);
      } else {
         if (countryData.length <= limit) {
            setData(countryData);
         }
      }
   }, [countryData]);

   // Handle outside click for search box
   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchQuery === '') setExpanded(false);
      },
   });

   // Memoize columns to prevent unnecessary re-renders
   const columns = useMemo(() => {
      return columnsData
         .filter((column) => columnVisibility[column.id])
         .map((column) =>
            columnHelper.accessor(column.id, {
               id: column.id,
               header: () => (
                  <Text
                     justifyContent="space-between"
                     align="center"
                     fontSize={{ sm: '10px', lg: '12px' }}
                     color="gray.400">
                     {column.header}
                  </Text>
               ),
               cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
            }),
         );
   }, [columnsData, columnVisibility]);

   // Memoize table instance
   const table = useReactTable({
      data,
      columns,
      state: { sorting },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   // Event handlers
   const handlePageChange = (directionOrPage) => {
      if (typeof directionOrPage === 'number') {
         // Direct page number navigation
         setCurrentPage(directionOrPage);
      } else {
         // Previous/next navigation
         const newPage = directionOrPage === 'next' ? pagination.nextPage : pagination.previousPage;
         if (newPage) setCurrentPage(newPage);
      }
   };

   const handlePageClick = () => {
      setIsEditingPage(true);
      setPageInputValue(''); // Clear input value when starting to edit
   };

   const handlePageInputSubmit = () => {
      const page = parseInt(pageInputValue);
      if (!isNaN(page) && page >= 1 && page <= pagination?.totalPages) {
         handlePageChange(page);
      }
      setIsEditingPage(false);
      setPageInputValue('');
   };

   const handlePageInputBlur = () => {
      handlePageInputSubmit();
   };

   const handlePageKeyDown = (e) => {
      if (e.key === 'Enter') {
         handlePageInputSubmit();
      } else if (e.key === 'Escape') {
         setIsEditingPage(false);
         setPageInputValue('');
      }
   };

   const handlePageSizeChange = (e) => {
      const value = e.target.value;
      const newLimit = value === 'ALL' ? pagination?.totalProducts : Number(value);
      setLimit(newLimit);
      setCurrentPage(1); // Reset to page 1 to avoid offset issues
   };

   const handleDelete = async () => {
      if (selectedRowId !== null) {
         try {
            await dispatch(deleteCountry(selectedRowId));
            dispatch(
               getCountries({
                  page: currentPage,
                  // limit: limit,
                  search: debouncedSearchQuery,
               }),
            );
            setDeleteDialogOpen(false);
         } catch (err) {
            console.error('Failed to delete Country:', err);
         }
      }
   };

   const handleDeleteClick = (id) => {
      setSelectedRowId(id);
      setDeleteDialogOpen(true);
   };

   const handleEditClick = (row) => {
      setCurrentRow(row);
      setIsEditMode(true);
      onOpen();
   };

   const handleAddNew = () => {
      setCurrentRow({});
      setIsEditMode(false);
      onOpen();
   };

   const handleSave = (formData) => {
      if (isEditMode && currentRow) {
         setData(data.map((row) => (row.id === currentRow.id ? { ...currentRow, ...formData } : row)));
      } else if (!isEditMode) {
         setData([...data, { ...formData, id: Date.now() }]);
      }
      onClose();
   };

   return (
      <Box bg={backgroundColor} w="100%">
         <Flex
            width={{ base: '100%', md: 'auto' }}
            mb="8px"
            px={{ base: '1px', md: '25px' }}
            justifyContent="space-between"
            align="center"
            overflowX="auto"
            flexWrap="nowrap"
            gap={3}>
            {/* Left Section: Title + Add New Button */}
            <Flex align="center" gap={4} flexShrink={0}>
               <Text
                  color={textColor}
                  ml={{ base: '8px', md: 0 }}
                  fontSize={{ base: '17px', lg: '22px' }}
                  fontWeight="700"
                  lineHeight="100%">
                  Countries
               </Text>
               {/* {!isMobileOrTablet && (Admin || countryRight?.add) && (
                  <Button
                     onClick={handleAddNew}
                     variant="darkBrand"
                     color="white"
                     fontSize="sm"
                     fontWeight="500"
                     borderRadius="full"
                     p={{ base: '4', lg: '5' }}>
                     Add New
                  </Button>
               )} */}
            </Flex>

            {/* Right Section: Pagination + Limit + Search + Menu */}
            <Flex align="center" gap={2} flexShrink={0}>
               {/* Search Field */}
               <MotionFlex
                  ref={wrapperRef}
                  align="center"
                  bg={bg}
                  borderRadius="full"
                  cursor="pointer"
                  px={expanded ? 3 : 0}
                  w={expanded ? '200px' : '40px'}
                  h="40px"
                  transition="all 0.3s ease"
                  onClick={() => setExpanded(true)}>
                  <MotionSearchIcon
                     color={icon}
                     animate={{ rotate: expanded ? 0 : 90 }}
                     transition={{ duration: 0.3 }}
                     style={{
                        marginRight: expanded ? '8px' : '10px',
                        marginLeft: expanded ? '0' : '10px',
                     }}
                  />
                  {expanded && (
                     <Input
                        variant="unstyled"
                        placeholder="Search..."
                        value={searchQuery}
                        color={icon}
                        // onChange={(e) => setSearchQuery(e.target.value)}
                        onChange={handleSearchChange}
                        autoFocus
                     />
                  )}
               </MotionFlex>
            </Flex>
         </Flex>

         {/* Table Rendering */}
         {isLoading ? (
            <Loader />
         ) : (
            <DynamicTable
               right={countryRight}
               table={table}
               data={countryData}
               emptyMessage="No country data found"
               onEditClick={handleEditClick}
               onDeleteClick={handleDeleteClick}
               canDelete={Admin || countryRight?.delete}
               columnVisibility={columnVisibility}
               pagename={false}
               columnsData={columnsData}
               sorting={sorting}
               limit={limit}
               setCurrentRow={setCurrentRow}
               onOpen={onOpen}
               setIsEditMode={setIsEditMode}
               // setIsOpenSingleView={setIsOpenSingleView}
               setDeleteDialogOpen={setDeleteDialogOpen}
               setSelectedRowId={setSelectedRowId}
               debouncedSearchQuery={debouncedSearchQuery}
               type="country"
            />
         )}

         {/* Form Component - Lazy Loaded */}
         <React.Suspense fallback={<Box p={4}>Loading form...</Box>}>
            {isOpen && (
               <CountryMasterForm
                  isOpen={isOpen}
                  onClose={onClose}
                  isEditMode={isEditMode}
                  currentRow={currentRow}
                  onSave={handleSave}
               />
            )}
         </React.Suspense>

         {/* Delete Confirmation Dialog */}
         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="Country"
         />
      </Box>
   );
}
